import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { TrendingUp, TrendingDown, Minus, ArrowUp, ArrowDown } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/auth/AuthProvider';
import { useMeasurementPreferences } from '@/hooks/useMeasurementPreferences';
import { 
  formatWeightChangeDisplay, 
  formatTimeImprovementDisplay, 
  formatPercentageDisplay 
} from '@/lib/measurementDisplay';

export const StatsOverview: React.FC = () => {
  const { user } = useAuth();
  const { preferences } = useMeasurementPreferences();

  const { data: bodyCompStats } = useQuery({
    queryKey: ['body-comp-stats'],
    queryFn: async () => {
      // First try to get user's data
      let { data, error } = await supabase
        .from('body_composition')
        .select('total_weight, body_fat_percentage, skeletal_muscle_mass, recorded_date')
        .order('recorded_date', { ascending: false })
        .limit(2);
      
      // If no user data found, get sample data
      if (!data || data.length === 0) {
        const { data: sampleData, error: sampleError } = await supabase
          .from('body_composition')
          .select('total_weight, body_fat_percentage, skeletal_muscle_mass, recorded_date')
          .eq('user_id', '00000000-0000-0000-0000-000000000001')
          .order('recorded_date', { ascending: false })
          .limit(2);
        
        if (sampleError) throw sampleError;
        data = sampleData;
      }
      
      if (error) throw error;
      
      if (data.length < 2) return null;
      
      const [latest, previous] = data;
      return {
        latest,
        previous,
        weightChange: latest.total_weight && previous.total_weight 
          ? latest.total_weight - previous.total_weight : null,
        bodyFatChange: latest.body_fat_percentage && previous.body_fat_percentage 
          ? latest.body_fat_percentage - previous.body_fat_percentage : null,
        muscleChange: latest.skeletal_muscle_mass && previous.skeletal_muscle_mass 
          ? latest.skeletal_muscle_mass - previous.skeletal_muscle_mass : null,
      };
    },
  });


  const { data: runningStats } = useQuery({
    queryKey: ['running-stats'],
    queryFn: async () => {
      // First try to get user's data
      let { data, error } = await supabase
        .from('running_performance')
        .select('time_seconds, distance_type, recorded_date')
        .eq('distance_type', '5k')
        .order('recorded_date', { ascending: false })
        .limit(2);
      
      // If no user data found, get sample data
      if (!data || data.length === 0) {
        const { data: sampleData, error: sampleError } = await supabase
          .from('running_performance')
          .select('time_seconds, distance_type, recorded_date')
          .eq('user_id', '00000000-0000-0000-0000-000000000001')
          .eq('distance_type', '5k')
          .order('recorded_date', { ascending: false })
          .limit(2);
        
        if (sampleError) throw sampleError;
        data = sampleData;
      }
      
      if (error) throw error;
      
      if (data.length < 2) return null;
      
      const [latest, previous] = data;
      return {
        latest,
        previous,
        timeImprovement: latest.time_seconds && previous.time_seconds 
          ? previous.time_seconds - latest.time_seconds : null,
      };
    },
  });

  const getTrendIcon = (value: number | null) => {
    if (!value) return <Minus className="h-4 w-4 text-gray-400" />;
    if (value > 0) return <TrendingUp className="h-4 w-4 text-green-600" />;
    if (value < 0) return <TrendingDown className="h-4 w-4 text-red-600" />;
    return <Minus className="h-4 w-4 text-gray-400" />;
  };

  const getTrendColor = (value: number | null, isWeight = false, isMuscle = false) => {
    if (!value) return "text-gray-600";
    if (isMuscle) {
      // For muscle mass: up is good (green), down is bad (red), no change is yellow
      if (value > 0) return "text-green-600";
      if (value < 0) return "text-red-600";
      return "text-yellow-600";
    }
    if (isWeight) {
      // For weight, trending down might be good
      return value > 0 ? "text-red-600" : "text-green-600";
    }
    // For body fat, trending down is good
    return value > 0 ? "text-red-600" : "text-green-600";
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        
        <Card className="hover:shadow-lg transition-all duration-200 border-l-4 border-l-primary/20">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Weight Trend</CardTitle>
          {getTrendIcon(bodyCompStats?.weightChange)}
        </CardHeader>
        <CardContent>
          <div className={`text-2xl font-bold ${getTrendColor(bodyCompStats?.weightChange, true)}`}>
            {bodyCompStats?.weightChange && bodyCompStats.latest && bodyCompStats.previous
              ? formatWeightChangeDisplay(
                  bodyCompStats.latest.total_weight!, 
                  bodyCompStats.previous.total_weight!, 
                  preferences
                ).value
              : 'No data'
            }
          </div>
          <p className="text-xs text-muted-foreground">
            Since last entry
          </p>
          {bodyCompStats?.latest && bodyCompStats?.previous && bodyCompStats?.latest.total_weight && bodyCompStats?.previous.total_weight && (
            <div className="flex items-center gap-1 mt-2">
              {(() => {
                const percentChange = ((bodyCompStats.latest.total_weight! - bodyCompStats.previous.total_weight!) / bodyCompStats.previous.total_weight!) * 100;
                const isPositive = percentChange >= 0;
                const colorClass = isPositive ? "text-red-600" : "text-green-600"; // For weight, down is good
                const Icon = isPositive ? ArrowUp : ArrowDown;
                
                return (
                  <>
                    <Icon className={`h-3 w-3 ${colorClass}`} />
                    <span className={`text-sm font-medium ${colorClass}`}>
                      {Math.abs(percentChange).toFixed(1)}%
                    </span>
                  </>
                );
              })()}
            </div>
          )}
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
            Track your total body weight changes over time
          </p>
        </CardContent>
      </Card>

      <Card className="hover:shadow-lg transition-all duration-200 border-l-4 border-l-blue-500/20">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Body Fat Trend</CardTitle>
          {getTrendIcon(bodyCompStats?.bodyFatChange)}
        </CardHeader>
        <CardContent>
          <div className={`text-2xl font-bold ${getTrendColor(bodyCompStats?.bodyFatChange)}`}>
            {bodyCompStats?.bodyFatChange 
              ? `${bodyCompStats.bodyFatChange > 0 ? '+' : ''}${formatPercentageDisplay(bodyCompStats.bodyFatChange)}`
              : 'No data'
            }
          </div>
          <p className="text-xs text-muted-foreground">
            Since last entry
          </p>
          {bodyCompStats?.latest?.body_fat_percentage && bodyCompStats?.previous?.body_fat_percentage && (
            <div className="flex items-center gap-1 mt-2">
              {(() => {
                const percentChange = ((bodyCompStats.latest.body_fat_percentage! - bodyCompStats.previous.body_fat_percentage!) / bodyCompStats.previous.body_fat_percentage!) * 100;
                const isPositive = percentChange >= 0;
                const colorClass = isPositive ? "text-red-600" : "text-green-600"; // For body fat, down is good
                const Icon = isPositive ? ArrowUp : ArrowDown;
                
                return (
                  <>
                    <Icon className={`h-3 w-3 ${colorClass}`} />
                    <span className={`text-sm font-medium ${colorClass}`}>
                      {Math.abs(percentChange).toFixed(1)}%
                    </span>
                  </>
                );
              })()}
            </div>
          )}
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
            Monitor your body fat percentage for healthier composition
          </p>
        </CardContent>
      </Card>

      <Card className="hover:shadow-lg transition-all duration-200 border-l-4 border-l-green-500/20">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Muscle Mass Trend</CardTitle>
          {getTrendIcon(bodyCompStats?.muscleChange)}
        </CardHeader>
        <CardContent>
          <div className={`text-2xl font-bold ${getTrendColor(bodyCompStats?.muscleChange, false, true)}`}>
            {bodyCompStats?.muscleChange && bodyCompStats.latest && bodyCompStats.previous
              ? formatWeightChangeDisplay(
                  bodyCompStats.latest.skeletal_muscle_mass!, 
                  bodyCompStats.previous.skeletal_muscle_mass!, 
                  preferences
                ).value
              : 'No data'
            }
          </div>
          <p className="text-xs text-muted-foreground">
            Since last entry
          </p>
          {bodyCompStats?.latest?.skeletal_muscle_mass && bodyCompStats?.previous?.skeletal_muscle_mass && (
            <div className="flex items-center gap-1 mt-2">
              {(() => {
                const percentChange = ((bodyCompStats.latest.skeletal_muscle_mass! - bodyCompStats.previous.skeletal_muscle_mass!) / bodyCompStats.previous.skeletal_muscle_mass!) * 100;
                const isPositive = percentChange >= 0;
                const colorClass = isPositive ? "text-green-600" : "text-red-600"; // For muscle mass, up is good
                const Icon = isPositive ? ArrowUp : ArrowDown;
                
                return (
                  <>
                    <Icon className={`h-3 w-3 ${colorClass}`} />
                    <span className={`text-sm font-medium ${colorClass}`}>
                      {Math.abs(percentChange).toFixed(1)}%
                    </span>
                  </>
                );
              })()}
            </div>
          )}
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
            Build and maintain lean muscle mass for strength
          </p>
        </CardContent>
      </Card>
      </div>

    </div>
  );
};
